import type {
  ComplaintAttachmentType,
  ComplaintChannel,
  ComplaintGroup,
  ComplaintListView,
  ComplaintProductType,
  ComplaintResolutionType,
  ComplaintSeverity,
  ComplaintSlaStatus,
  ComplaintState,
  ComplaintType,
} from "@/api/types";

export type DsTone = "primary" | "amber" | "destructive" | "muted" | "neutral" | "success";

export const STATE_LABEL: Record<ComplaintState, string> = {
  draft: "Чернетка",
  submitted: "Подано",
  in_progress: "В роботі",
  awaiting_client_response: "Очікує клієнта",
  pending_resolution_approval: "На погодженні рішення",
  resolution_approved: "Рішення погоджено",
  resolved: "Вирішено",
  closed: "Закрито",
  rejected: "Відхилено",
  escalated: "Ескальовано",
};

export const STATE_TONE: Record<ComplaintState, DsTone> = {
  draft: "muted",
  submitted: "primary",
  in_progress: "primary",
  awaiting_client_response: "amber",
  pending_resolution_approval: "amber",
  resolution_approved: "success",
  resolved: "success",
  closed: "muted",
  rejected: "destructive",
  escalated: "destructive",
};

export const SEVERITY_LABEL: Record<ComplaintSeverity, string> = {
  low: "Низька",
  medium: "Середня",
  high: "Висока",
  critical: "Критична",
};

export const SEVERITY_TONE: Record<ComplaintSeverity, DsTone> = {
  low: "muted",
  medium: "neutral",
  high: "amber",
  critical: "destructive",
};

export const TYPE_LABEL: Record<ComplaintType, string> = {
  // [M] manager bucket
  simple_service: "Сервісне звернення",
  document_error: "Помилка в документі",
  delivery_logistics: "Доставка / логістика",
  order_error: "Помилка в замовленні",
  pricing_terms: "Ціна / умови",
  packaging_damage_no_quality: "Пошкодження упаковки",
  // [SQ] CRM_SupportQuality queue bucket
  product_quality: "Якість товару",
  food_quality: "Якість корму",
  batch_issue: "Проблема партії",
  expiry_issue: "Термін придатності",
  health_risk: "Ризик здоров’ю",
  recurring_sku_issue: "Повторна проблема SKU",
  // triage
  other: "Інше",
};

/** [SQ] queue bucket — routed to CRM_SupportQuality (BS-2). */
export const SUPPORT_QUALITY_TYPES: ComplaintType[] = [
  "product_quality",
  "food_quality",
  "batch_issue",
  "expiry_issue",
  "health_risk",
  "recurring_sku_issue",
];

/** Types where the product block is intrinsic (cCLinkedToProduct auto-on). */
export const PRODUCT_LINKED_TYPES: ComplaintType[] = [...SUPPORT_QUALITY_TYPES];

/** Manager-bucket types that may optionally involve a product (user toggles). */
export const OPTIONAL_PRODUCT_TYPES: ComplaintType[] = [
  "delivery_logistics",
  "order_error",
  "packaging_damage_no_quality",
];

/** R3 (§2.7) — full product block required at submit. */
export const CONDITIONAL_REQUIRED_TYPES: ComplaintType[] = [
  "food_quality",
  "batch_issue",
  "expiry_issue",
  "health_risk",
];

export const CHANNEL_LABEL: Record<ComplaintChannel, string> = {
  email: "Email",
  phone: "Телефон",
  in_person: "Особисто",
  messenger: "Месенджер",
  written_letter: "Лист",
  manager_observation: "Спостереження менеджера",
  external_referral: "Зовнішнє звернення",
};

export const PRODUCT_TYPE_LABEL: Record<ComplaintProductType, string> = {
  food: "Корм",
  treats: "Ласощі",
  litter: "Наповнювач",
  hygiene: "Гігієна",
  accessory: "Аксесуар",
  veterinary_product: "Ветпрепарат",
  other: "Інше",
};

export const RESOLUTION_TYPE_LABEL: Record<ComplaintResolutionType, string> = {
  refund_full: "Повне повернення",
  refund_partial: "Часткове повернення",
  product_replacement: "Заміна товару",
  credit_note: "Кредит-нота",
  discount_next_order: "Знижка на наступне замовлення",
  service_apology: "Вибачення",
  explanation_only: "Роз’яснення",
  escalated_to_director: "Передано директору",
  rejected_not_valid: "Відхилено (необґрунтовано)",
  other: "Інше",
};

/** Financial resolution types — UI requires an amount for these. */
export const FINANCIAL_RESOLUTION_TYPES: ComplaintResolutionType[] = [
  "refund_full",
  "refund_partial",
  "credit_note",
  "discount_next_order",
];

export const ATTACHMENT_TYPE_LABEL: Record<ComplaintAttachmentType, string> = {
  customer_email: "Лист клієнта",
  photo_evidence: "Фото-доказ",
  video_evidence: "Відео-доказ",
  document: "Документ",
  correspondence: "Листування",
  resolution_proof: "Підтвердження рішення",
  other_attachment: "Інше",
  supplier_response: "Відповідь постачальника",
};

export const QUEUE_LABEL: Record<string, string> = {
  manager: "Менеджер клієнта",
  // COMP-UX W4 — business language (was "Черга SupportQuality").
  supportQuality: "Служба якості / підтримки",
  manual_assignment: "Без призначення",
};

export const QUEUE_TONE: Record<string, DsTone> = {
  manager: "primary",
  supportQuality: "amber",
  manual_assignment: "destructive",
};

export const SLA_TONE: Record<ComplaintSlaStatus, DsTone> = {
  on_track: "success",
  due_soon: "amber",
  breached: "destructive",
  met: "muted",
  none: "muted",
};

export interface ComplaintViewDef {
  k: ComplaintListView;
  label: string;
  /** Sub-label under the group name (lifecycle layout v1.1 cards). */
  sub: string;
  /** COMP-LIFECYCLE — count comes from backend groupCounts[groupKey] (W1a, never page-derived). */
  groupKey?: ComplaintGroup;
}

// COMP-LIFECYCLE layout v1.1 — Level-1 = 6 business-group cards (lifecycle order) + «Усі».
// The single «Стадія» axis. Срочність-флаги (Потребує дії / Прострочено) live in a separate
// thin «Потребує уваги» bar, NOT as competing stage cards (the old 5-KPI grid is removed).
export const VIEW_DEFS: ComplaintViewDef[] = [
  { k: "drafts", label: "Чернетки", sub: "мої, не подані", groupKey: "drafts" },
  { k: "new", label: "Нові", sub: "подано", groupKey: "new" },
  { k: "in_work", label: "В роботі", sub: "обробляється", groupKey: "in_work" },
  { k: "waiting", label: "Очікує відповіді", sub: "клієнт/керівник", groupKey: "waiting" },
  { k: "decided", label: "Прийнято рішення", sub: "рішення є", groupKey: "decided" },
  { k: "done", label: "Закрито", sub: "завершено", groupKey: "done" },
  { k: "all", label: "Усі", sub: "всі стадії" },
];

export const SCOPE_LABEL: Record<string, string> = {
  my: "Мої",
  team: "Команда",
  all: "Усі",
  manual_queue: "Без призначення",
  sq_queue: "Служба якості",
};

export function stateLabel(s: ComplaintState): string {
  return STATE_LABEL[s] ?? s;
}

export function stateTone(s: ComplaintState): DsTone {
  return STATE_TONE[s] ?? "muted";
}

export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("uk-UA", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function formatMoney(amount: number | null, currency = "UAH"): string {
  if (amount == null) return "—";
  return `${amount.toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}
